import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, X } from 'lucide-react';
import {
  SUPPORTED_LANGUAGES,
  detectBrowserLanguage,
  persistLanguage,
  LANG_SUGGESTION_DISMISSED,
} from '../i18n';

/** Public job routes have their own dedicated Google-Translate banner. */
const isPublicJobRoute = (pathname) => {
  if (!pathname) return false;
  const segments = pathname.split('/').filter(Boolean);
  return segments.length >= 2 && (segments[1] === 'jobs' || segments[1] === 'about');
};

/**
 * Shows a subtle "Prefer German? Switch language" banner if the visitor's browser
 * language is supported but differs from the currently active i18n language.
 * Persists user's dismissal (or acceptance) so we don't nag on every page.
 */
const LanguageWelcomeBanner = () => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (isPublicJobRoute(location.pathname)) {
      setVisible(false);
      return () => { cancelled = true; };
    }
    const check = () => {
      try {
        const dismissed = localStorage.getItem(LANG_SUGGESTION_DISMISSED);
        if (dismissed) return;
      } catch { /* noop */ }
      const browser = detectBrowserLanguage();
      const active = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
      if (!browser || browser === active) return;
      // Only prompt if user has never actively chosen (no localStorage lang set)
      let stored = null;
      try { stored = localStorage.getItem('jobasti_lang'); } catch { /* noop */ }
      if (stored) return;
      if (cancelled) return;
      setSuggestion(browser);
      setVisible(true);
    };
    // Give i18n a tick to resolve
    const id = setTimeout(check, 300);
    return () => { cancelled = true; clearTimeout(id); };
  }, [i18n.language, i18n.resolvedLanguage, location.pathname]);

  const dismiss = () => {
    try { localStorage.setItem(LANG_SUGGESTION_DISMISSED, '1'); } catch { /* noop */ }
    setVisible(false);
  };

  const accept = () => {
    if (suggestion) persistLanguage(suggestion);
    try { localStorage.setItem(LANG_SUGGESTION_DISMISSED, '1'); } catch { /* noop */ }
    setVisible(false);
  };

  if (!visible || !suggestion) return null;

  const langMeta = SUPPORTED_LANGUAGES.find((l) => l.code === suggestion);
  if (!langMeta) return null;

  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] max-w-md w-[92%] bg-white border border-gray-200 rounded-2xl shadow-2xl px-4 py-3.5 flex items-center gap-3 animate-in slide-in-from-bottom-4 fade-in duration-300"
      data-testid="lang-suggestion-banner"
      role="dialog"
      aria-label="Language suggestion"
    >
      <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
        <Globe className="w-4 h-4 text-blue-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13.5px] text-gray-900 font-semibold leading-tight">
          {t('lang.suggestTitle', { language: langMeta.label })}
        </p>
        <p className="text-[11.5px] text-gray-500 mt-0.5">{t('lang.suggestSubtitle')}</p>
      </div>
      <button
        type="button"
        onClick={accept}
        className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold rounded-lg px-3 py-2 transition"
        data-testid="lang-suggestion-accept"
      >
        <span className="text-base leading-none">{langMeta.flag}</span>
        {langMeta.label}
      </button>
      <button
        type="button"
        onClick={dismiss}
        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-full transition"
        aria-label="Dismiss"
        data-testid="lang-suggestion-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default LanguageWelcomeBanner;
